import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import type { ProjectSpecialVo, PurchaseDemandVo } from "@zhihaoscm/entity";
import { Alert, Button, Card, Cascader, Form, Input, Select, Space, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { RouterLink } from "@/views/components/RouterLink";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { intendService } from "@/api/services/intendService";
import { projectService } from "@/api/services/projectService";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { TABLE_COLUMN_WIDTH } from "@/config/width";
import { INTEND_STATE, PURCHASE_TYPE } from "@/meta/purchase/intend";
import { dateFormat } from "@/utils";
import { areaLib } from "@/utils/areaLib";
import { useUserStore } from "@/views/stores";
import { formatNumber } from "@/utils/number";
import AssignDialog, {
    type AssignDialogRef,
    type assignSubmitData
} from "@/views/components/AssignDialog/AssignDialog";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { getProdTypeEnum } from "@/views/pages/tradeManage/goodsManagement/components";
import CreateProject from "./intendedDemand/components/createProject";

export interface IntendedDemandRegionOption {
    label: string;
    value: string | number;
    children?: IntendedDemandRegionOption[];
}

export interface IntendedDemandProductTypeOption {
    label: string;
    value: string | number;
}

export interface IntendedDemandRow {
    purchaseDemand?: {
        id?: number | string;
        code?: string;
        purType?: number | string;
        productName?: string;
        productTypeName?: string;
        productTypeSpec?: string | number;
        purQuantity?: number | string;
        totalPrice?: number | string;
        purRatio?: number | string;
        provinceCode?: string;
        cityCode?: string;
        regionCode?: string;
        createdTime?: string;
        confirmationState?: number | string;
        state?: number | string;
    };
    user?: { id?: number | string; name?: string };
    product?: { productTypeSpec?: string | number };
}

export interface IntendedDemandQuery {
    page: number;
    size: number;
    state: number[];
    param?: string;
    purType?: number;
    productTypeId?: string | number;
    provinceCode?: string | number;
    cityCode?: string | number;
    regionCode?: string | number;
    startTime?: string;
    endTime?: string;
}

export interface IntendedDemandPageResult {
    list?: IntendedDemandRow[];
    total?: number;
}

export interface IntendedDemandSpecial {
    id?: number | string;
    name?: string;
    user?: { id?: number | string; personId?: number | string; name?: string };
    demandCount?: number;
}

export interface IntendedDemandPageProps {
    loadDemands?: (query: IntendedDemandQuery) => Promise<IntendedDemandPageResult>;
    loadSpecials?: (keyword: string) => Promise<IntendedDemandSpecial[]>;
    loadProductTypes?: () => Promise<IntendedDemandProductTypeOption[]>;
    regionOptions?: IntendedDemandRegionOption[];
    loadDemandForProject?: (id: number | string) => Promise<PurchaseDemandVo>;
    loadProjectSpecials?: () => Promise<ProjectSpecialVo[]>;
    designate?: (form: { id: number | string; userId: number | string }) => Promise<unknown>;
    startIntend?: (id: number | string) => Promise<unknown>;
    submitAudit?: (id: number | string) => Promise<unknown>;
    strand?: (form: { id: number | string; opinion: string }) => Promise<unknown>;
    resetIntend?: (id: number | string) => Promise<unknown>;
    closeIntend?: (id: number | string) => Promise<unknown>;
    permissions?: string[];
}

const defaultQuery: IntendedDemandQuery = { page: 1, size: 10, state: [] };

/** 复用 Vue 品类筛选器的异步枚举数据。 */
export async function loadIntendedDemandProductTypes(): Promise<IntendedDemandProductTypeOption[]> {
    return (await getProdTypeEnum()) || [];
}

/** 生成项目操作需要完整意向数据，沿用 Vue `/vo/:id` 接口。 */
export async function loadIntendedDemandForProject(id: number | string): Promise<PurchaseDemandVo> {
    return intendService.get(id);
}

/** 生成项目弹窗沿用 Vue 项目专员选择接口。 */
export async function loadIntendedDemandProjectSpecials(): Promise<ProjectSpecialVo[]> {
    return projectService.getSpecialsSelector("");
}

/** 复用 Vue 意向需求分页接口及 `/admin/purchase-demand/paging` 路径。 */
export async function loadIntendedDemands(query: IntendedDemandQuery): Promise<IntendedDemandPageResult> {
    const response = await request.service.get<IntendedDemandPageResult>("/admin/purchase-demand/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 指派弹窗的专员查询接口。 */
export async function loadIntendedDemandSpecials(keyword: string): Promise<IntendedDemandSpecial[]> {
    const response = await request.service.get<IntendedDemandSpecial[]>(
        `/admin/purchase-demand/find/service?name=${encodeURIComponent(keyword)}`
    );
    return response.data;
}

/** 复用 Vue 意向需求指派接口及 payload。 */
export async function designateIntendedDemand(form: { id: number | string; userId: number | string }) {
    const response = await request.service.post("/admin/purchase-demand/assign", form);
    return response.data;
}

/** 复用 Vue 意向需求开始处理接口。 */
export async function startIntendedDemand(id: number | string) {
    const response = await request.service.put(`/admin/purchase-demand/start/${id}`);
    return response.data;
}

/** 复用 Vue 意向需求提交审核接口。 */
export async function submitIntendedDemandAudit(id: number | string) {
    const response = await request.service.post(`/admin/purchase-demand/submit/${id}`);
    return response.data;
}

/** 复用 Vue 意向需求搁浅接口及原因 payload。 */
export async function strandIntendedDemand(form: { id: number | string; opinion: string }) {
    const response = await request.service.post("/admin/purchase-demand/stranding", form);
    return response.data;
}

/** 复用 Vue 意向需求重置接口。 */
export async function resetIntendedDemand(id: number | string) {
    const response = await request.service.post(`/admin/purchase-demand/revoke/${id}`);
    return response.data;
}

/** 复用 Vue 意向需求关闭接口。 */
export async function closeIntendedDemand(id: number | string) {
    const response = await request.service.put(`/admin/purchase-demand/close/${id}`);
    return response.data;
}

/** 将后端可能返回的字符串枚举统一为采购页面的数字枚举。 */
function normalizeRows(rows?: IntendedDemandRow[]): IntendedDemandRow[] {
    return (rows || []).map(row => ({
        ...row,
        purchaseDemand: row.purchaseDemand
            ? {
                  ...row.purchaseDemand,
                  purType: row.purchaseDemand.purType == null ? undefined : Number(row.purchaseDemand.purType),
                  state: row.purchaseDemand.state == null ? undefined : Number(row.purchaseDemand.state)
              }
            : undefined
    }));
}

interface IntendedDemandFilterValues {
    param?: string;
    purType?: number;
    productTypeId?: string | number;
    receiveArea?: Array<string | number>;
    createdTime?: [string, string] | null;
    state?: number | number[];
}

/** 把日期范围转换为 Vue handleTableParam 最终提交的 UTC 日界值。 */
function toUtcDayBoundary(value: string, boundary: "start" | "end"): string {
    return `${value}T${boundary === "start" ? "00:00:00" : "23:59:59"}.000Z`;
}

/** 将 Vue 表单值转换为分页接口参数，筛选后始终从第一页开始。 */
function buildQuery(values: IntendedDemandFilterValues): IntendedDemandQuery {
    const query: IntendedDemandQuery = { ...defaultQuery };
    if (values.param?.trim()) query.param = values.param.trim();
    if (values.purType != null) query.purType = Number(values.purType);
    if (values.productTypeId != null) query.productTypeId = values.productTypeId;
    if (values.receiveArea?.length) {
        [query.provinceCode, query.cityCode, query.regionCode] = values.receiveArea;
    }
    if (values.createdTime?.[0] && values.createdTime[1]) {
        query.startTime = toUtcDayBoundary(values.createdTime[0], "start");
        query.endTime = toUtcDayBoundary(values.createdTime[1], "end");
    }
    const stateValues = Array.isArray(values.state)
        ? values.state.map(Number)
        : values.state == null
          ? []
          : [Number(values.state)];
    if (stateValues.includes(INTEND_STATE.UN_CLOSED.code)) {
        // Vue 旧版把“未关闭”作为前端筛选快捷项，不把 0 发送给后端。
        query.state = stateValues.includes(INTEND_STATE.CLOSED.code) ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] : [];
    } else {
        query.state = stateValues;
    }
    return query;
}

function getEnumLabel(type: "purchase" | "state", value?: number | string): string {
    if (value == null || value === "") return "--";
    return type === "purchase"
        ? PURCHASE_TYPE.getIntroByCode(Number(value))
        : INTEND_STATE.getIntroByCode(Number(value));
}

/** 沿用 Vue isShowBtn 的状态矩阵，避免 React 列表操作因迁移遗漏而错显。 */
export function isIntendedDemandActionVisible(state: number | string | undefined, action: string): boolean {
    const stateCode = Number(state);
    const stateMap: Record<string, number[]> = {
        指派: [
            INTEND_STATE.STAY_ASSIGN.code,
            INTEND_STATE.PENDING.code,
            INTEND_STATE.REJECTED.code,
            INTEND_STATE.PROCESSING.code
        ],
        开始: [INTEND_STATE.PENDING.code],
        提交审核: [INTEND_STATE.PROCESSING.code],
        修改: [
            INTEND_STATE.STAY_ASSIGN.code,
            INTEND_STATE.PENDING.code,
            INTEND_STATE.REJECTED.code,
            INTEND_STATE.PROCESSING.code,
            INTEND_STATE.WAIT_CONFIRM.code
        ],
        搁浅: [
            INTEND_STATE.PENDING.code,
            INTEND_STATE.REJECTED.code,
            INTEND_STATE.PROCESSING.code,
            INTEND_STATE.WAIT_CONFIRM.code
        ],
        审核: [INTEND_STATE.WAIT_AUDIT.code],
        生成项目: [INTEND_STATE.CONFIRMED.code],
        重置: [INTEND_STATE.CONFIRMED.code, INTEND_STATE.STRANDING.code],
        关闭: [INTEND_STATE.STRANDING.code]
    };
    return stateMap[action]?.includes(stateCode) ?? false;
}

/** 意向需求列表，完整复用 Vue 的筛选、分页、表格列和操作契约。 */
export function IntendedDemandPage({
    loadDemands = loadIntendedDemands,
    loadSpecials = loadIntendedDemandSpecials,
    loadProductTypes = loadIntendedDemandProductTypes,
    regionOptions,
    loadDemandForProject = loadIntendedDemandForProject,
    loadProjectSpecials = loadIntendedDemandProjectSpecials,
    designate = designateIntendedDemand,
    startIntend = startIntendedDemand,
    submitAudit = submitIntendedDemandAudit,
    strand = strandIntendedDemand,
    resetIntend = resetIntendedDemand,
    closeIntend = closeIntendedDemand,
    permissions
}: IntendedDemandPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const userInfo = useUserStore(state => state.userInfo);
    const currentUserId = (userInfo.user as { id?: number | string } | undefined)?.id;
    const effectivePermissions = permissions ?? storePermissions;
    const canManage = effectivePermissions.includes("ROLE_PURCHASE_DEMAND_MANAGE");
    const canDeal = effectivePermissions.includes("ROLE_PURCHASE_DEMAND_DEAL");
    const [filterForm] = Form.useForm();
    const [rows, setRows] = useState<IntendedDemandRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<IntendedDemandQuery>(defaultQuery);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [productTypeOptions, setProductTypeOptions] = useState<IntendedDemandProductTypeOption[]>([]);
    const [resolvedRegionOptions, setResolvedRegionOptions] = useState<IntendedDemandRegionOption[]>(
        () => regionOptions || (areaLib.areaList2Options(areaLib.getAreaList()) as IntendedDemandRegionOption[])
    );
    const assignDialogRef = useRef<AssignDialogRef>(null);
    const [strandRow, setStrandRow] = useState<IntendedDemandRow | null>(null);
    const [strandReason, setStrandReason] = useState("");
    const [projectVisible, setProjectVisible] = useState(false);
    const [projectDemand, setProjectDemand] = useState<PurchaseDemandVo>();
    const [projectSpecials, setProjectSpecials] = useState<ProjectSpecialVo[]>([]);

    /** 重新加载当前筛选条件，保持 Vue 操作成功后的列表刷新行为。 */
    const refresh = async () => {
        try {
            const result = await loadDemands(query);
            setRows(normalizeRows(result.list));
            setTotal(result.total || 0);
            setErrorMessage(null);
        } catch {
            setErrorMessage("意向需求加载失败");
        }
    };

    /** 执行列表操作并统一处理接口失败提示。 */
    const runAction = async (action: () => Promise<unknown>) => {
        try {
            await action();
            await refresh();
        } catch {
            setErrorMessage("意向需求操作失败");
        }
    };

    /** 在路由上下文缺失的轻量测试/嵌入场景中仍保留旧页面的导航目标。 */
    const navigate = (to: string) => {
        if (typeof window === "undefined") return;
        window.history.pushState({}, "", to);
        window.dispatchEvent(new PopStateEvent("popstate"));
    };

    useEffect(() => {
        let disposed = false;
        void loadProductTypes()
            .then(options => {
                if (!disposed) setProductTypeOptions(options || []);
            })
            .catch(() => {
                if (!disposed) setProductTypeOptions([]);
            });
        return () => {
            disposed = true;
        };
    }, [loadProductTypes]);

    useEffect(() => {
        if (regionOptions) {
            setResolvedRegionOptions(regionOptions);
            return;
        }
        const syncRegionOptions = () =>
            setResolvedRegionOptions(areaLib.areaList2Options(areaLib.getAreaList()) as IntendedDemandRegionOption[]);
        syncRegionOptions();
        const unsubscribe = areaLib.subscribe(syncRegionOptions);
        void areaLib.ready.then(syncRegionOptions);
        return () => {
            unsubscribe();
        };
    }, [regionOptions]);

    useEffect(() => {
        let disposed = false;
        void loadDemands(query)
            .then(result => {
                if (disposed) return;
                setRows(normalizeRows(result.list));
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("意向需求加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDemands, query]);

    /** 将当前迁移期的扁平专员数据归一为旧 AssignDialog 的卡片数据结构。 */
    const searchAssignableSpecials = async (keyword: string) => {
        const result = await loadSpecials(keyword);
        return (result || [])
            .map(special => {
                const personId = special.user?.personId ?? special.user?.id ?? special.id;
                if (personId == null) return null;
                return {
                    ...special,
                    user: {
                        personId: Number(personId),
                        name: special.user?.name || special.name || String(personId)
                    }
                };
            })
            .filter((special): special is NonNullable<typeof special> => special !== null);
    };

    /** 打开 Vue 同款指派卡片弹窗，并保留当前专员回显。 */
    const openAssignDialog = (row: IntendedDemandRow) => {
        if (!row.purchaseDemand) return;
        void assignDialogRef.current?.openDialog({
            info: row.purchaseDemand,
            tips: "跟进中的采购意向数量",
            title: "指派服务专员",
            countFiled: "demandCount",
            searchSpecial: searchAssignableSpecials,
            curOfficerId: row.user?.id == null ? undefined : Number(row.user.id)
        });
    };

    /** 指派确认沿用 Vue assign payload，并在成功后刷新当前页。 */
    const submitAssign = (data: assignSubmitData) => {
        const id = (data.info as IntendedDemandRow["purchaseDemand"])?.id;
        if (id == null || data.officerId == null) return;
        void runAction(() => designate({ id, userId: data.officerId! }));
    };

    /** 生成项目操作先加载旧弹窗依赖数据，避免迁移成错误的独立页面跳转。 */
    const openCreateProject = async (id: number | string) => {
        try {
            const [demand, specials] = await Promise.all([loadDemandForProject(id), loadProjectSpecials()]);
            setProjectDemand(demand);
            setProjectSpecials(specials || []);
            setProjectVisible(true);
            setErrorMessage(null);
        } catch {
            setErrorMessage("生成项目数据加载失败");
        }
    };

    // 用 zhihao-ui-react 列契约承接 Vue 意向需求的嵌套字段、枚举、详情入口和金额格式。
    const columns: TableColumnProps<IntendedDemandRow>[] = [
        {
            key: "code",
            label: "编号",
            width: 150,
            prop: "code",
            render: ({ row }) => {
                const demand = row.purchaseDemand;
                return demand?.id && demand.code ? (
                    <RouterLink to={`/purchasingManage/intendedDemand/detail/${demand.id}`}>{demand.code}</RouterLink>
                ) : (
                    demand?.code || "--"
                );
            }
        },
        {
            key: "purType",
            label: "采购类型",
            width: 108,
            prop: "purType",
            render: ({ row }) => (
                <div className="pur-type d-flex">{getEnumLabel("purchase", row.purchaseDemand?.purType)}</div>
            )
        },
        {
            key: "goodsNameAndProdType",
            label: "商品名称 / 品类",
            minWidth: 140,
            prop: "goodsNameAndProdType",
            render: ({ row }) => {
                const demand = row.purchaseDemand;
                const productTypeSpec = row.product?.productTypeSpec ?? demand?.productTypeSpec;
                const productType = demand?.productTypeName
                    ? `${demand.productTypeName}${productTypeSpec == null || productTypeSpec === "" ? "" : `(${productTypeSpec})`}`
                    : "--";
                return (
                    <div className="mul-line-cell d-flex">
                        <span className="intend--goods-name">{demand?.productName || "--"}</span>
                        <span className="intend--prod-type mul-line-cell__low">{productType}</span>
                    </div>
                );
            }
        },
        {
            key: "purQuantity",
            label: "采购数量 (吨)",
            minWidth: TABLE_COLUMN_WIDTH.WORD6,
            prop: "purQuantity",
            render: ({ row }) => formatNumber(row.purchaseDemand?.purQuantity, 0)
        },
        {
            key: "totalPrice",
            label: "采购总价 (元)",
            minWidth: 168,
            prop: "totalPrice",
            render: ({ row }) => {
                const demand = row.purchaseDemand;
                const totalPrice = Number(demand?.totalPrice || 0);
                const purchaserContribution = (Number(demand?.purRatio || 0) * totalPrice) / 100;
                return (
                    <div className="mul-line-cell d-flex">
                        <span className="total-price">{formatNumber(demand?.totalPrice, 2)}</span>
                        {Number(demand?.purType) === PURCHASE_TYPE.INDIRECT.code && totalPrice ? (
                            <span className="indirect-price mul-line-cell__low">
                                采购商出资{formatNumber(purchaserContribution, 2)}
                            </span>
                        ) : null}
                    </div>
                );
            }
        },
        {
            key: "receiveArea",
            label: "收货区域",
            minWidth: 140,
            prop: "receiveArea",
            render: ({ row }) => {
                const demand = row.purchaseDemand;
                return areaLib.getFullAreaName(demand?.provinceCode, demand?.cityCode, demand?.regionCode) || "--";
            }
        },
        {
            key: "createdTime",
            label: "提交时间",
            width: 160,
            prop: "createdTime",
            render: ({ row }) => dateFormat(row.purchaseDemand?.createdTime, DATETIME_PICKER_FORMAT)
        },
        { key: "user", label: "服务专员", minWidth: 80, prop: "user", render: ({ row }) => row.user?.name || "--" },
        {
            key: "upDownStreamConfirm",
            label: "用户确认",
            width: 216,
            prop: "upDownStreamConfirm",
            render: ({ row }) => {
                const demand = row.purchaseDemand;
                const confirmationState = Number(demand?.confirmationState || 0);
                const confirmRole = (label: string, bit: number) => (
                    <div className="d-flex">
                        <i
                            aria-hidden="true"
                            className={`iconfont icon-a-right1 ${confirmationState & bit ? "up-stream" : "no-confirm"}`}
                        />
                        <span className="role">{label}</span>
                    </div>
                );
                return (
                    <div className="confirm-state d-flex">
                        {confirmRole("供应商", 2)}
                        {Number(demand?.purType) === PURCHASE_TYPE.INDIRECT.code ? confirmRole("联合购销商", 4) : null}
                        {confirmRole("采购商", 1)}
                    </div>
                );
            }
        },
        {
            key: "state",
            label: "状态",
            width: TABLE_COLUMN_WIDTH.WORD3,
            prop: "state",
            render: ({ row }) => getEnumLabel("state", row.purchaseDemand?.state)
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 320,
            prop: "operation",
            render: ({ row }) => {
                const demand = row.purchaseDemand;
                const id = demand?.id;
                if (!demand || id == null) return "--";
                const state = demand.state;
                const ownerAllowed = currentUserId != null && String(currentUserId) === String(row.user?.id);
                const actionButton = (label: string, onClick: () => void) => (
                    <Button aria-label={label} disabled={banButton} size="small" type="link" onClick={onClick}>
                        {label}
                    </Button>
                );
                return (
                    <Space size={0} wrap>
                        {canManage && isIntendedDemandActionVisible(state, "指派")
                            ? actionButton("指派", () => openAssignDialog(row))
                            : null}
                        {canDeal && ownerAllowed && isIntendedDemandActionVisible(state, "开始")
                            ? actionButton("开始", () => void runAction(() => startIntend(id)))
                            : null}
                        {canDeal && ownerAllowed && isIntendedDemandActionVisible(state, "提交审核")
                            ? actionButton("提交审核", () => void runAction(() => submitAudit(id)))
                            : null}
                        {canDeal && ownerAllowed && isIntendedDemandActionVisible(state, "修改")
                            ? actionButton(
                                  "修改",
                                  () => void navigate(`/purchasingManage/intendedDemand/intendAdd/${id}`)
                              )
                            : null}
                        {canDeal && ownerAllowed && isIntendedDemandActionVisible(state, "搁浅")
                            ? actionButton("搁浅", () => {
                                  setStrandReason("");
                                  setStrandRow(row);
                              })
                            : null}
                        {canManage && isIntendedDemandActionVisible(state, "审核")
                            ? actionButton(
                                  "审核",
                                  () => void navigate(`/purchasingManage/intendedDemand/detail/${id}?audit=1`)
                              )
                            : null}
                        {canManage && isIntendedDemandActionVisible(state, "生成项目")
                            ? actionButton("生成项目", () => void openCreateProject(id))
                            : null}
                        {canManage && isIntendedDemandActionVisible(state, "重置")
                            ? actionButton("重置", () => void runAction(() => resetIntend(id)))
                            : null}
                        {canManage && isIntendedDemandActionVisible(state, "关闭")
                            ? actionButton("关闭", () => void runAction(() => closeIntend(id)))
                            : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="intended-demand-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<IntendedDemandRow>
                    data={rows}
                    // 意向需求操作顺序与旧 Vue 一致，完整显示所有状态动作。
                    columns={columns}
                    pagination={false}
                    rowKey={row => String(row.purchaseDemand?.id || row.purchaseDemand?.code)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    showOverflowTooltip
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                意向需求
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                initialValues={{ state: [INTEND_STATE.UN_CLOSED.code] }}
                                layout="inline"
                                onFinish={values => setQuery(buildQuery(values))}
                            >
                                {canManage ? (
                                    <Button
                                        className="admin-table-primary-action"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => navigate("/purchasingManage/intendedDemand/intendAdd")}
                                    >
                                        新增
                                    </Button>
                                ) : null}
                                <Form.Item label="编号" name="param">
                                    <Input
                                        aria-label="意向编号或商品关键词"
                                        placeholder="请输入意向编号、商品关键词搜索"
                                        maxLength={32}
                                    />
                                </Form.Item>
                                <Form.Item label="采购类型" name="purType">
                                    <Select
                                        aria-label="采购类型"
                                        allowClear
                                        options={PURCHASE_TYPE.options}
                                        placeholder="请选择采购类型"
                                        style={{ minWidth: 140 }}
                                    />
                                </Form.Item>
                                <Form.Item label="商品名称 / 品类" name="productTypeId">
                                    <Select
                                        aria-label="砂石品类"
                                        allowClear
                                        filterOption
                                        options={productTypeOptions}
                                        placeholder="请选择砂石品类"
                                        showSearch
                                    />
                                </Form.Item>
                                <Form.Item label="收货区域" name="receiveArea">
                                    <Cascader
                                        aria-label="收货区域"
                                        changeOnSelect
                                        options={resolvedRegionOptions}
                                        placeholder="请选择区域"
                                        showSearch
                                    />
                                </Form.Item>
                                <Form.Item label="提交时间" name="createdTime">
                                    <ZhDateRangePicker
                                        format="YYYY-MM-DD"
                                        rangeSeparator="-"
                                        startPlaceholder="起始日期"
                                        endPlaceholder="截止日期"
                                        type="daterange"
                                        valueFormat="YYYY-MM-DD"
                                    />
                                </Form.Item>
                                <Form.Item label="状态" name="state">
                                    <Select
                                        aria-label="意向状态"
                                        allowClear
                                        mode="multiple"
                                        options={INTEND_STATE.options}
                                        placeholder="请选择状态"
                                        style={{ minWidth: 140 }}
                                    />
                                </Form.Item>
                                <Button
                                    aria-label="搜索意向需求"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={() => {
                                        filterForm.resetFields();
                                        setQuery(defaultQuery);
                                    }}
                                />
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                onChange={(page, size) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: size !== current.size ? 1 : page,
                                        size
                                    }))
                                }
                            />
                        </div>
                    }
                />
                <AssignDialog ref={assignDialogRef} onSubmit={submitAssign} />
                <ZhDialog
                    open={strandRow !== null}
                    title="提示"
                    width={520}
                    onCancel={() => setStrandRow(null)}
                    footer={
                        <Space>
                            <Button onClick={() => setStrandRow(null)}>取消</Button>
                            <Button
                                disabled={!strandReason.trim() || banButton}
                                type="primary"
                                onClick={() => {
                                    const id = strandRow?.purchaseDemand?.id;
                                    const opinion = strandReason.trim();
                                    if (id == null || !opinion) return;
                                    setStrandRow(null);
                                    void runAction(() => strand({ id, opinion }));
                                }}
                            >
                                确定
                            </Button>
                        </Space>
                    }
                >
                    <label className="block text-sm">
                        <span>您确定要搁浅此意向需求吗</span>
                        <Input.TextArea
                            className="mt-2"
                            maxLength={32}
                            placeholder="请输入搁浅原因"
                            rows={3}
                            showCount
                            value={strandReason}
                            onChange={event => setStrandReason(event.target.value)}
                        />
                    </label>
                </ZhDialog>
                <CreateProject
                    visible={projectVisible}
                    projectCommissionerList={projectSpecials}
                    purchaseDemandVo={projectDemand}
                    onUpdateVisible={setProjectVisible}
                    onConfirmed={() => void refresh()}
                />
            </Card>
        </section>
    );
}
